import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Menu, ShoppingCart, Heart, Search, ChevronDown, Pill, Sparkles, TrendingUp, Package, Tag, X, User } from "lucide-react";
import { MarketSelector } from "@/components/MarketSelector";
import { NavLink } from "@/components/NavLink";
import { SearchModal } from "@/components/SearchModal";
import { CartSidebar } from "@/components/cart/CartSidebar";
import { useCart } from "@/contexts/CartContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMarket } from "@/contexts/MarketContext";
import logo from '@/assets/logo.png';
import { supabase } from "@/integrations/supabase/client";
import { useCategories, useProducts } from "@/hooks/use-cms-api";

interface Product {
  id: string;
  title: string;
  slug: string;
  price: number;
  image_url: string | null;
  category: string | null;
}

export function Header() {
  const { itemCount } = useCart();
  const { favorites } = useFavorites();
  const { formatPrice } = useMarket();
  const favoritesCount = Array.from(favorites).length;
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showTestsDropdown, setShowTestsDropdown] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [hoveredQuickLink, setHoveredQuickLink] = useState<string | null>(null);
  const [headerLogo, setHeaderLogo] = useState<string>(logo);
  const [announcementText, setAnnouncementText] = useState<string>('Order before 2:00 PM - your package will be shipped the same day!');
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(null);

  // Fetch data from CMS API
  const { data: cmsCategories } = useCategories();
  const { data: productsData } = useProducts({ limit: 200 });


  const products = useMemo(() => productsData?.products || [], [productsData]);

  // Get all active categories first
  const allCategories = useMemo(() => {
    return (cmsCategories || [])
      .filter(c => c.is_active)
      .map(c => ({ name: c.name, slug: c.slug, image_url: c.image_url }));
  }, [cmsCategories]);

  // Filter categories to only show those with products
  const categories = useMemo(() => {
    return allCategories.filter(category => {
      return products.some(p => {
        const catSlug = p.category?.toLowerCase().replace(/\s+/g, '-') || '';
        return catSlug === category.slug;
      });
    });
  }, [allCategories, products]);

  // Group products by category
  const categoryProducts = useMemo(() => {
    const grouped: Record<string, Product[]> = {};
    products.forEach(p => {
      const catSlug = p.category?.toLowerCase().replace(/\s+/g, '-') || '';
      if (catSlug) {
        if (!grouped[catSlug]) grouped[catSlug] = [];
        if (grouped[catSlug].length < 3) grouped[catSlug].push(p as Product);
      }
    });
    return grouped;
  }, [products]);


  const bestSellersProducts = useMemo(() => products.slice(0, 3), [products]);
  const newArrivalsProducts = useMemo(() => products.slice(0, 3), [products]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('header_logo_url, announcement_text')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (data?.header_logo_url) {
        setHeaderLogo(data.header_logo_url);
      }
      if (data?.announcement_text) {
        setAnnouncementText(data.announcement_text);
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setShowTestsDropdown(true);
  };

  const handleDropdownMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowTestsDropdown(false);
    }, 100);
    setDropdownTimeout(timeout);
  };

  return (
    <>
      {/* Top Header Bar - Desktop Only */}
      <div className="hidden lg:block bg-white border-b border-border/30">
        <div className="container mx-auto px-6 py-1.5">
          <div className="flex items-center justify-between text-sm">
            {/* Announcement - Left */}
            <div className="flex items-center gap-2 text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span>{announcementText}</span>
            </div>
            
            {/* Login Icon - Right */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a 
                    href="https://my.gettested.io/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 transition-colors mr-2 flex items-center gap-2"
                    aria-label="Login to account"
                  >
                    <span className="text-sm font-medium">Account</span>
                    <User className="h-5 w-5" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Login to your account</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="sticky top-0 z-50 bg-card border-b py-3">
        <div className="container mx-auto px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <NavLink 
              to="/"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.location.href = '/';
                }
              }}
            >
              <img src={headerLogo} alt="GetTested" className="h-10" />
            </NavLink>
            
            {/* Health Panels Dropdown on Desktop - Moved to left */}
            <div 
              className="hidden lg:block relative"
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <Button 
                variant="ghost" 
                className="flex items-center gap-1.5 group relative px-3 py-2 border-b-2 border-transparent hover:border-primary hover:bg-accent/50 transition-all duration-200"
                onClick={() => navigate('/products')}
              >
                Supplements
                <ChevronDown className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" />
              </Button>
              
              {/* Full-Width Dropdown Menu */}
              {showTestsDropdown && (
                <div 
                  className="fixed left-0 right-0 z-[9999]"
                  style={{ top: isScrolled ? '4rem' : '5.5rem' }}
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <div className="container mx-auto px-6">
                    <div className="bg-card border border-border/50 rounded-lg shadow-2xl p-6">
                    <div className="grid grid-cols-3 gap-6">
                      {/* Column 1 - Shop by Category */}
                      <div className="border-r border-border/30 pr-6">
                        <h3 className="text-sm font-semibold mb-4 text-foreground flex items-center gap-2">
                          <Tag className="h-5 w-5 text-primary" />
                          Shop by Category
                        </h3>
                        <div className="space-y-1">
                          {categories.map((category) => (
                            <button
                              key={category.slug}
                              className="block w-full text-left px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                              onMouseEnter={() => {
                                setHoveredCategory(category.slug);
                              }}
                              onClick={() => {
                                setShowTestsDropdown(false);
                                navigate(`/products?categories=${encodeURIComponent(category.name)}`);
                              }}
                            >
                              {category.name}
                            </button>
                          ))}
                        </div>
                        <NavLink
                          to="/products"
                          className="block mt-4 px-3 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                          onClick={() => setShowTestsDropdown(false)}
                        >
                          View All Supplements →
                        </NavLink>
                      </div>

                      {/* Column 2 - Quick Links */}
                      <div className="border-r border-border/30 pr-6">
                        <h3 className="text-sm font-semibold mb-4 text-foreground flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-primary" />
                          Quick Links
                        </h3>
                        
                        <div className="space-y-1">
                          <NavLink
                            to="/products?sort=popular"
                            className="block px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                            onMouseEnter={() => {
                              setHoveredQuickLink('best-sellers');
                              setHoveredCategory(null);
                            }}
                            onClick={() => setShowTestsDropdown(false)}
                          >
                            Best Sellers
                          </NavLink>
                          <NavLink
                            to="/products?sort=newest"
                            className="block px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                            onMouseEnter={() => {
                              setHoveredQuickLink('new-arrivals');
                              setHoveredCategory(null);
                            }}
                            onClick={() => setShowTestsDropdown(false)}
                          >
                            New Arrivals
                          </NavLink>
                        </div>
                        
                        <NavLink
                          to="/products"
                          className="block mt-4 px-3 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                          onClick={() => setShowTestsDropdown(false)}
                        >
                          View All Supplements →
                        </NavLink>
                        
                      </div>

                      {/* Column 3 - Large Preview Area */}
                      <div className="col-span-1 bg-muted/30 rounded-lg p-6 min-h-[300px]">
                        {(hoveredCategory || hoveredQuickLink) ? (
                          <div className="space-y-4 w-full">
                            {/* Preview Header */}
                            <div className="border-b border-border pb-3">
                              <h3 className="text-sm font-semibold text-foreground">
                                {hoveredCategory 
                                  ? categories.find(c => c.slug === hoveredCategory)?.name 
                                  : hoveredQuickLink === 'best-sellers' ? 'Best Sellers' : 'New Arrivals'
                                }
                              </h3>
                            </div>
                            
                            {/* Products List */}
                            <div className="space-y-3">
                              {(hoveredCategory 
                                ? categoryProducts[hoveredCategory]
                                : hoveredQuickLink === 'best-sellers'
                                ? bestSellersProducts
                                : hoveredQuickLink === 'new-arrivals'
                                ? newArrivalsProducts
                                : []
                              )?.slice(0, 5).map((product) => (
                              <NavLink
                                key={product.id}
                                to={`/product/${product.slug}`}
                                className="flex gap-3 p-3 rounded-md hover:bg-background group"
                                onClick={() => setShowTestsDropdown(false)}
                              >
                                <div className="w-16 h-16 flex-shrink-0 bg-muted rounded flex items-center justify-center overflow-hidden">
                                  {product.image_url ? (
                                    <img 
                                      src={product.image_url} 
                                      alt={product.title}
                                      className="w-16 h-16 object-cover rounded"
                                    />
                                  ) : (
                                    <Package className="h-8 w-8 text-muted-foreground/40" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                                    {product.title}
                                  </p>
                                  <p className="text-sm text-primary font-semibold mt-1">
                                    {formatPrice(product.price)}
                                  </p>
                                </div>
                              </NavLink>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-center space-y-3">
                            <Pill className="h-24 w-24 text-muted-foreground/40" />
                            <p className="text-sm text-muted-foreground">
                              Hover over a category to see top products
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
          </div>
          
          <div className="flex items-center gap-2">
            <NavLink 
              to="/brands"
              className="hidden lg:flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              Brands
            </NavLink>
            <NavLink 
              to="/products"
              className="hidden lg:flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              View all Supplements
            </NavLink>
            <div className="hidden lg:block">
              <MarketSelector />
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="flex-shrink-0"
              onClick={() => setShowSearchModal(true)}
              aria-label="Search products"
            >
              <Search className="h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => navigate('/favorites')}
              aria-label={`Favorites${favoritesCount > 0 ? ` (${favoritesCount} items)` : ''}`}
            >
              <Heart className="h-5 w-5" />
              {favoritesCount > 0 && (
                <Badge 
                  variant="secondary" 
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                >
                  {favoritesCount}
                </Badge>
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setShowCartSidebar(true)}
              aria-label={`Shopping cart${itemCount > 0 ? ` (${itemCount} items)` : ''}`}
            >
              <ShoppingCart className="h-5 w-5" />
              {itemCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                >
                  {itemCount}
                </Badge>
              )}
            </Button>
            
            <Button
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setShowMobileMenu(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <SearchModal open={showSearchModal} onOpenChange={setShowSearchModal} />
      <CartSidebar open={showCartSidebar} onClose={() => setShowCartSidebar(false)} />

      {/* Mobile Menu Sheet - Full Screen */}
      <Sheet open={showMobileMenu} onOpenChange={setShowMobileMenu}>
        <SheetContent side="right" className="w-full sm:max-w-full p-0 flex flex-col">
          {/* Fixed Header */}
          <SheetHeader className="px-4 py-3 border-b bg-card z-10 flex-shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-base">Menu</SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowMobileMenu(false)}
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>
          
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-4">
              {/* Quick Actions - Always Visible */}
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="/products?sort=popular"
                  className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <TrendingUp className="h-5 w-5" />
                  <span className="text-sm font-medium">Best Sellers</span>
                </a>
                <a
                  href="/products"
                  className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg bg-accent hover:bg-accent/80 transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Package className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">All Products</span>
                </a>
              </div>

              {/* Collapsible Categories */}
              <div className="border rounded-lg overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <Pill className="h-5 w-5 text-primary" />
                      <span className="font-semibold">Shop by Category</span>
                    </div>
                    <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="border-t bg-muted/30 p-3 space-y-1">
                    {categories.length > 0 ? (
                      <>
                        {categories.map((category) => (
                          <a
                            key={category.slug}
                            href={`/products?categories=${encodeURIComponent(category.name)}`}
                            className="block p-3 rounded-md hover:bg-background transition-colors"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            <div className="font-medium text-sm">{category.name}</div>
                          </a>
                        ))}
                        <a
                          href="/products"
                          className="block p-3 mt-2 rounded-md bg-background hover:bg-accent transition-colors text-center border-t"
                          onClick={() => setShowMobileMenu(false)}
                        >
                          <span className="text-sm font-medium text-primary">View All →</span>
                        </a>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Loading categories...
                      </p>
                    )}
                  </div>
                </details>
              </div>

              {/* Additional Links */}
              <div className="space-y-2 pt-2">
                <a
                  href="/products?new=true"
                  className="flex items-center gap-3 p-4 rounded-lg border hover:bg-accent transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Sparkles className="h-5 w-5 text-primary" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">New Arrivals</div>
                    <div className="text-xs text-muted-foreground">Latest products</div>
                  </div>
                </a>
                <a
                  href="/products?sale=true"
                  className="flex items-center gap-3 p-4 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Tag className="h-5 w-5 text-primary" />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-primary">Special Offers</div>
                    <div className="text-xs text-muted-foreground">Limited deals</div>
                  </div>
                </a>
                <NavLink
                  to="/favorites"
                  className="flex items-center gap-3 p-4 rounded-lg border hover:bg-accent transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Heart className="h-5 w-5 text-primary" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">My Favorites</div>
                    {favoritesCount > 0 && (
                      <div className="text-xs text-muted-foreground">{favoritesCount} items</div>
                    )}
                  </div>
                </NavLink>
              </div>

              {/* Market Selector */}
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-medium">Market</span>
                </div>
                <MarketSelector />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
